/**
 * Sort trigger button with dropdown for search modals.
 *
 * Renders a pressable sort label (shows "Sort" when no selection, or
 * the active field label with a direction arrow) and an absolutely-positioned
 * SortDropdown beneath it. Used as the sortTrigger slot in SearchToolbar.
 * @module components/shared/search-modal/SortTrigger/SortTriggerView
 */

import { ArrowDownIcon, ArrowUpIcon } from "lucide-react-native";
import React, { memo } from "react";

import SortDropdown from "@/components/organisms/shared/search-modal/SortDropdown";
import {
  ACTIVE_BUTTON_CLASS,
  DEFAULT_BUTTON_CLASS,
} from "@/components/organisms/shared/search-modal/SearchToolbar";
import { Box } from "@/components/atoms/Box";
import { Icon } from "@/components/atoms/Icon";
import { Pressable } from "@/components/atoms/Pressable";
import { Text } from "@/components/atoms/Text";
import type { SortOption } from "@/features/scouting/types/scouting-target";

/** Props for the sort dropdown panel state */
export interface SortDropdownProps {
  /** Whether the dropdown is open */
  readonly isOpen: boolean;
  /** Available sort options */
  readonly sortOptions: readonly SortOption[];
  /** Currently selected sort field — null when no explicit selection */
  readonly selectedSortField: string | null;
  /** Currently selected sort direction */
  readonly sortDirection: string;
  /** Pre-built handlers keyed by sort field */
  readonly sortHandlers: Readonly<Record<string, () => void>>;
  /** Toggle ascending */
  readonly onAscending: () => void;
  /** Toggle descending */
  readonly onDescending: () => void;
  /** Close the dropdown */
  readonly onClose: () => void;
}

/** Props for SortTriggerView */
interface SortTriggerViewProps {
  /** Toggle sort dropdown handler */
  readonly onToggleSort: () => void;
  /** Sort dropdown state and handlers */
  readonly sortDropdownProps: SortDropdownProps;
  /** Current sort field display label */
  readonly sortFieldLabel: string;
}

/**
 * Sort trigger button and dropdown for search modals.
 * @param root0 - Component props
 * @param root0.onToggleSort - Toggle sort dropdown handler
 * @param root0.sortDropdownProps - Sort dropdown state and handlers
 * @param root0.sortFieldLabel - Current sort field display label
 */
const SortTriggerView = ({
  onToggleSort,
  sortDropdownProps,
  sortFieldLabel,
}: SortTriggerViewProps) => (
  <Box
    className="relative"
    {...({ "data-sort-dropdown": "true" } as Record<string, string>)}
  >
    <Pressable
      onPress={onToggleSort}
      className={
        sortDropdownProps.selectedSortField === null
          ? DEFAULT_BUTTON_CLASS
          : ACTIVE_BUTTON_CLASS
      }
    >
      <Text
        variant="title-sm-medium"
        className={`${
          sortDropdownProps.selectedSortField === null
            ? "text-content-muted"
            : "text-accent-primary"
        }`}
      >
        {sortDropdownProps.selectedSortField === null ? "Sort" : sortFieldLabel}
      </Text>
      {sortDropdownProps.selectedSortField === null ? null : (
        <Icon
          as={
            sortDropdownProps.sortDirection === "ASC"
              ? ArrowUpIcon
              : ArrowDownIcon
          }
          size="sm"
          className="text-accent-primary"
        />
      )}
    </Pressable>
    <SortDropdown
      isOpen={sortDropdownProps.isOpen}
      sortOptions={sortDropdownProps.sortOptions}
      selectedSortField={sortDropdownProps.selectedSortField}
      sortDirection={sortDropdownProps.sortDirection as "ASC" | "DESC"}
      sortHandlers={sortDropdownProps.sortHandlers}
      onAscending={sortDropdownProps.onAscending}
      onDescending={sortDropdownProps.onDescending}
    />
  </Box>
);

SortTriggerView.displayName = "SortTriggerView";

export default memo(SortTriggerView);
